<script setup>
import { ref, computed, nextTick, onMounted, onBeforeUnmount } from 'vue';
import { useRoute } from 'vue-router';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import {
  fetchMarketplaceJson,
  postMarketplaceJsonBody,
  postMarketplaceBlob,
} from 'dashboard/helpers/marketplaceApi';

const route = useRoute();
const accountId = () => route.params.accountId;

// ---------------------------------------------------------------------------
// DURUM — mode: 'realtime' (OpenAI Realtime/WebRTC, asıl deneyim) | 'classic'
// (eski STT→LLM→TTS zinciri, otomatik YEDEK). status her iki modda ortak.
// Erişilebilirlik ilkesi: her durumun SESLİ karşılığı var (earcon + gerektiğinde
// sözlü duyuru); ekran yalnızca ikincil destek.
// ---------------------------------------------------------------------------
const mode = ref('realtime');
const status = ref('idle'); // idle|connecting|listening|thinking|speaking|error
const connected = ref(false);
const question = ref('');
const answer = ref('');
const liveAnswer = ref(''); // realtime akan transkript
const errorMsg = ref('');
const infoMsg = ref('');
const textInput = ref('');
const history = ref([]); // klasik mod bağlamı

// KONUŞMA MODU (2026-09-21): "en ufacık tık sesinde duruyor" şikâyetinin çözümü.
//  serbest      → doğal konuşma, sözünü kesebilirsin (gürültü filtresi açık)
//  kesme_kapali → asistan cümlesini bitirir; konuşurken mikrofon KAPATILIR,
//                 böylece ortam sesi sözünü fiziksel olarak kesemez
//  bas_konus    → ses algılama tamamen kapalı; yalnız butona basılı tutunca dinler
const KONUSMA_MODLARI = [
  {
    key: 'serbest',
    label: 'Serbest konuşma',
    ipucu: 'Doğal konuş, sözünü kesebilirsin',
  },
  {
    key: 'kesme_kapali',
    label: 'Sözümü kesme',
    ipucu: 'Gürültülü ortam için: cümlesini bitirir',
  },
  {
    key: 'bas_konus',
    label: 'Bas-konuş',
    ipucu: 'En sessiz yol: basılı tutarken dinler',
  },
];
const konusmaModu = ref('serbest');
const pttBasili = ref(false);
const micSeviye = ref(0); // 0..1 — mikrofondan gelen ses gücü
const sesSeviye = ref(0); // 0..1 — asistanın konuşma gücü
const aracEtiketi = ref(''); // "Ekip karnesine bakıyorum…"
const dokum = ref([]); // konuşma dökümü: {rol, metin}

// Araç adlarını insan diline çevirir — kullanıcı neye bakıldığını duysun/görsün.
const ARAC_ADLARI = {
  ekip_karnesi: 'ekip karnesi',
  cevapsiz_sohbetler: 'cevapsız sohbetler',
  uretim_plani: 'üretim planı',
  reklam_gunluk_karne: 'günlük reklam karnesi',
  reklam_ozeti: 'reklam özeti',
  satis_ozeti: 'satış özeti',
  kar_ozeti: 'kâr hesabı',
  gercek_kar_zarar: 'şirket kâr/zarar',
  stok_detay: 'stok dökümü',
  urun_durumu: 'ürün durumu',
  kargo_durumu: 'kargo durumu',
  gunluk_brifing: 'günlük brifing',
  gunu_degerlendir: 'günün değerlendirmesi',
  derin_analiz: 'derin analiz',
};
const aracAdi = n => ARAC_ADLARI[n] || (n || '').replace(/_/g, ' ');

// Yeni satır gelince döküm dibe kaysın — kullanıcı elle kaydırmak zorunda kalmasın.
const akisKutusu = ref(null);
const dibeKay = () => {
  nextTick(() => {
    const el = akisKutusu.value;
    if (el) el.scrollTop = el.scrollHeight;
  });
};

const dokumeEkle = (rol, metin) => {
  const t = (metin || '').trim();
  if (!t) return;
  const son = dokum.value[dokum.value.length - 1];
  if (son && son.rol === rol && son.metin === t) return;
  dokum.value.push({
    rol,
    metin: t,
    id: `${rol}-${dokum.value.length}-${t.length}`,
  });
  if (dokum.value.length > 40) dokum.value.shift();
  dibeKay();
};

const STATUS_TEXT = {
  idle: 'Hazır. Başlamak için büyük butona dokunun.',
  connecting: 'Bağlanıyorum…',
  listening: 'Dinliyorum, konuşabilirsiniz.',
  thinking: 'Düşünüyorum, verilere bakıyorum…',
  speaking: 'Cevap veriyorum. Sözümü kesebilirsiniz.',
  error: 'Bir sorun oldu.',
};
const statusText = computed(() => STATUS_TEXT[status.value] || '');

// ---------------------------------------------------------------------------
// SES ALTYAPISI + EARCON'LAR (durumların sesli işaretleri — dosyasız, WebAudio)
// ---------------------------------------------------------------------------
let audioCtx = null;
let thinkTimer = null;
let micStream = null; // ses altyapısı bölümünde: micAc() bundan önce tanımlı olamaz
// Ses gücü ölçerler: ekrandaki canlı halkayı besler. Kullanıcı "beni duyuyor
// mu" sorusunun cevabını GÖREREK alsın diye var (eskiden hiçbir geri bildirim yoktu).
let micAnalyser = null;
let outAnalyser = null;
let levelRaf = null;

const seviyeOlc = analyser => {
  if (!analyser) return 0;
  const buf = new Uint8Array(analyser.frequencyBinCount);
  analyser.getByteTimeDomainData(buf);
  let toplam = 0;
  for (let i = 0; i < buf.length; i += 1) {
    const v = (buf[i] - 128) / 128;
    toplam += v * v;
  }
  return Math.min(1, Math.sqrt(toplam / buf.length) * 4);
};

const seviyeDonguBaslat = () => {
  if (levelRaf) return;
  const tick = () => {
    micSeviye.value = micSeviye.value * 0.6 + seviyeOlc(micAnalyser) * 0.4;
    sesSeviye.value = sesSeviye.value * 0.6 + seviyeOlc(outAnalyser) * 0.4;
    levelRaf = requestAnimationFrame(tick);
  };
  levelRaf = requestAnimationFrame(tick);
};

const seviyeDonguDurdur = () => {
  if (levelRaf) cancelAnimationFrame(levelRaf);
  levelRaf = null;
  micSeviye.value = 0;
  sesSeviye.value = 0;
};

// Mikrofon hattını fiziksel olarak aç/kapat. "Sözümü kesme" modunda asistan
// konuşurken kapatılır: böylece ortam gürültüsü sözünü kesemez, tarayıcı
// tarafında garanti altına alınır (sunucu ayarına ek güvenlik).
const micAc = acik => {
  if (!micStream) return;
  micStream.getAudioTracks().forEach(t => {
    t.enabled = acik;
  });
};

const analyserBagla = stream => {
  if (!audioCtx || !stream) return null;
  try {
    const src = audioCtx.createMediaStreamSource(stream);
    const an = audioCtx.createAnalyser();
    an.fftSize = 512;
    src.connect(an);
    return an;
  } catch {
    return null;
  }
};

const ensureAudio = () => {
  if (!audioCtx) {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (Ctx) audioCtx = new Ctx();
  }
  if (audioCtx && audioCtx.state === 'suspended') audioCtx.resume();
};

const playTone = (freq, dur = 0.12, delay = 0, gainV = 0.14) => {
  if (!audioCtx) return;
  const t = audioCtx.currentTime + delay;
  const osc = audioCtx.createOscillator();
  const gain = audioCtx.createGain();
  osc.type = 'sine';
  osc.frequency.value = freq;
  gain.gain.setValueAtTime(gainV, t);
  gain.gain.exponentialRampToValueAtTime(0.001, t + dur);
  osc.connect(gain);
  gain.connect(audioCtx.destination);
  osc.start(t);
  osc.stop(t + dur + 0.02);
};

const stopThinkTick = () => {
  if (thinkTimer) {
    clearInterval(thinkTimer);
    thinkTimer = null;
  }
};

const earcon = name => {
  ensureAudio();
  if (!audioCtx) return;
  if (name === 'connect') {
    playTone(523, 0.1);
    playTone(659, 0.12, 0.12);
  } else if (name === 'ready') {
    playTone(880, 0.09);
  } else if (name === 'listen') {
    playTone(660, 0.07, 0, 0.1);
  } else if (name === 'think') {
    stopThinkTick();
    playTone(440, 0.06, 0, 0.07);
    thinkTimer = setInterval(() => playTone(440, 0.06, 0, 0.07), 1600);
  } else if (name === 'error') {
    stopThinkTick();
    playTone(392, 0.14);
    playTone(262, 0.2, 0.16);
  } else if (name === 'disconnect') {
    stopThinkTick();
    playTone(494, 0.1);
    playTone(392, 0.1, 0.12);
    playTone(262, 0.16, 0.24);
  }
};

// ---------------------------------------------------------------------------
// GERÇEK ZAMANLI MOTOR (OpenAI Realtime, WebRTC). Anahtar frontend'e inmez:
// backend realtime_session kısa ömürlü token verir; araç çağrıları
// assistant_tool endpoint'i üzerinden auth'lu backend'de çalışır.
// ---------------------------------------------------------------------------
let pc = null;
let dc = null;
let remoteAudio = null;
let reconnectTried = false;
let manualClose = false;

const dcSend = obj => {
  if (dc && dc.readyState === 'open') dc.send(JSON.stringify(obj));
};

const cleanupRealtime = () => {
  stopThinkTick();
  try {
    if (dc) dc.close();
  } catch {
    /* kapalı olabilir */
  }
  try {
    if (pc) pc.close();
  } catch {
    /* kapalı olabilir */
  }
  if (micStream) micStream.getTracks().forEach(t => t.stop());
  if (remoteAudio) remoteAudio.srcObject = null;
  // Ses ölçerler ölü akışı göstermesin (mod değişiminde halka takılı kalıyordu).
  seviyeDonguDurdur();
  micAnalyser = null;
  outAnalyser = null;
  pttBasili.value = false;
  aracEtiketi.value = '';
  dc = null;
  pc = null;
  micStream = null;
  connected.value = false;
};

const fallbackToClassic = reason => {
  cleanupRealtime();
  mode.value = 'classic';
  status.value = 'idle';
  const msg = `${reason} Klasik sesli moda geçtim; butona basıp konuşabilirsiniz.`;
  infoMsg.value = msg;
  earcon('error');
  // Klasik motor asagida tanimli; calisma zamaninda hazir olur.
  // eslint-disable-next-line no-use-before-define
  speakClassic(msg); // sessiz bırakma: geçişi SESLE duyur
};

// derin_analiz sonucunu bekle (Sidekiq → Redis → poll).
const pollDeepResult = async analizId => {
  for (let i = 0; i < 50; i += 1) {
    // eslint-disable-next-line no-await-in-loop
    await new Promise(r => {
      setTimeout(r, 3000);
    });
    try {
      // eslint-disable-next-line no-await-in-loop
      const data = await fetchMarketplaceJson(
        accountId(),
        `meta_ads/deep_result?analiz_id=${analizId}`
      );
      if (data.durum === 'tamam' || data.durum === 'hata') return data;
    } catch {
      /* geçici ağ hatası: denemeye devam */
    }
  }
  return { durum: 'hata', mesaj: 'Derin analiz zaman aşımına uğradı.' };
};

const sendToolOutput = (callId, obj) => {
  dcSend({
    type: 'conversation.item.create',
    item: {
      type: 'function_call_output',
      call_id: callId,
      output: JSON.stringify(obj),
    },
  });
};

const runTool = async item => {
  try {
    const data = await postMarketplaceJsonBody(
      accountId(),
      'meta_ads/assistant_tool',
      { name: item.name, args: JSON.parse(item.arguments || '{}') }
    );
    return data.result;
  } catch {
    return { hata: 'araç şu an çalıştırılamadı, kullanıcıya söyle' };
  }
};

// Derin analiz: "başladı" cevabı verildikten sonra sonuç hazır olunca modele
// ayrı bir sistem mesajı olarak iletilir, model sesli özetler.
const followUpDeepAnalysis = async result => {
  earcon('think');
  const final = await pollDeepResult(result.analiz_id);
  stopThinkTick();
  const text =
    final.durum === 'tamam'
      ? `SİSTEM: Derin analiz sonucu hazır. Kullanıcıya ana bulguları sesli ve akıcı biçimde aktar:\n${final.metin}`
      : `SİSTEM: Derin analiz başarısız oldu (${final.mesaj}). Kullanıcıya kibarca söyle ve daha basit bir soruyla yardımcı olmayı öner.`;
  dcSend({
    type: 'conversation.item.create',
    item: {
      type: 'message',
      role: 'user',
      content: [{ type: 'input_text', text }],
    },
  });
  dcSend({ type: 'response.create' });
};

// KRİTİK: Model birden çok aracı PARALEL çağırabilir (ör. 3 reklam hesabı).
// TÜM sonuçlar toplanmadan response.create GÖNDERİLMEZ — yoksa model ilk
// sonuçla konuşmaya başlayıp kalan hesaplar için "veri çekemedim" diyor ve
// sonraki response.create'ler "aktif cevap var" hatasına düşüyordu.
const handleFunctionCalls = async items => {
  status.value = 'thinking';
  earcon('think');
  // Kullanıcı neye bakıldığını görsün/duysun: sessiz bekleme hissi kalkar.
  aracEtiketi.value = items.map(i => aracAdi(i.name)).join(', ');
  const results = await Promise.all(
    items.map(async item => ({ item, out: await runTool(item) }))
  );
  results.forEach(({ item, out }) => sendToolOutput(item.call_id, out));
  dcSend({ type: 'response.create' }); // hepsi hazır → TEK cevap
  results
    .filter(
      ({ item, out }) => item.name === 'derin_analiz' && out && out.analiz_id
    )
    .forEach(({ out }) => followUpDeepAnalysis(out));
};

const handleServerEvent = ev => {
  switch (ev.type) {
    case 'input_audio_buffer.speech_started':
      stopThinkTick();
      status.value = 'listening';
      break;
    case 'conversation.item.input_audio_transcription.completed':
      if (ev.transcript) {
        question.value = ev.transcript.trim();
        dokumeEkle('soru', ev.transcript);
      }
      break;
    case 'response.created':
      status.value = 'thinking';
      earcon('think');
      liveAnswer.value = '';
      break;
    case 'response.output_audio_transcript.delta':
      stopThinkTick();
      status.value = 'speaking';
      liveAnswer.value += ev.delta || '';
      break;
    case 'response.output_audio_transcript.done':
      if (ev.transcript) {
        answer.value = ev.transcript;
        dokumeEkle('cevap', ev.transcript);
      }
      break;
    case 'output_audio_buffer.started':
      stopThinkTick();
      status.value = 'speaking';
      // "Sözümü kesme" modunda asistan konuşurken mikrofon kapanır.
      if (konusmaModu.value === 'kesme_kapali') micAc(false);
      break;
    case 'output_audio_buffer.stopped':
    case 'output_audio_buffer.cleared':
      if (status.value === 'speaking') status.value = 'listening';
      aracEtiketi.value = '';
      if (konusmaModu.value === 'kesme_kapali') micAc(true);
      break;
    case 'response.done': {
      stopThinkTick();
      const calls = ((ev.response && ev.response.output) || []).filter(
        i => i.type === 'function_call'
      );
      if (calls.length) {
        handleFunctionCalls(calls);
      } else if (status.value === 'thinking') {
        status.value = 'listening';
      }
      break;
    }
    case 'error':
      // Oturum içi tekil hatalar ölümcül değil; sesli işaret ver, devam et.
      earcon('error');
      break;
    default:
      break;
  }
};

const connectRealtime = async () => {
  ensureAudio();
  manualClose = false;
  errorMsg.value = '';
  infoMsg.value = '';
  status.value = 'connecting';
  earcon('connect');

  let token;
  try {
    token = await postMarketplaceJsonBody(
      accountId(),
      `meta_ads/realtime_session?mod=${konusmaModu.value}&hassasiyet=dusuk`,
      {}
    );
  } catch {
    fallbackToClassic('Hızlı ses bağlantısı şu an kurulamadı.');
    return;
  }

  try {
    // HAM mikrofon ALINMAZ: hoparlörden çıkan kendi sesi mikrofona geri girip
    // "kullanıcı konuşuyor" sanılıyordu ve asistan kendi kendini kesiyordu.
    // Yankı engelleme + gürültü bastırma + otomatik seviye şart.
    micStream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
        channelCount: 1,
      },
    });
  } catch {
    status.value = 'error';
    errorMsg.value =
      'Mikrofon izni alınamadı. Tarayıcı ayarlarından mikrofona izin verin.';
    earcon('error');
    return;
  }

  try {
    pc = new RTCPeerConnection();
    remoteAudio = remoteAudio || new Audio();
    remoteAudio.autoplay = true;
    pc.ontrack = e => {
      const [stream] = e.streams;
      remoteAudio.srcObject = stream;
      outAnalyser = analyserBagla(stream);
      seviyeDonguBaslat();
    };
    micStream.getTracks().forEach(t => pc.addTrack(t, micStream));
    micAnalyser = analyserBagla(micStream);
    seviyeDonguBaslat();
    // Bas-konuş modunda mikrofon BAŞTAN kapalıdır: kullanıcı butona basana
    // kadar hiçbir ses gönderilmez, dolayısıyla hiçbir gürültü tetikleyemez.
    if (konusmaModu.value === 'bas_konus') micAc(false);

    dc = pc.createDataChannel('oai-events');
    dc.onmessage = e => {
      try {
        handleServerEvent(JSON.parse(e.data));
      } catch {
        /* bozuk olay: yok say */
      }
    };
    dc.onopen = () => {
      connected.value = true;
      reconnectTried = false;
      status.value = 'listening';
      earcon('ready');
      infoMsg.value = 'Bağlandım. Konuşabilirsiniz — sözümü kesebilirsiniz.';
    };

    pc.onconnectionstatechange = () => {
      if (!pc || manualClose) return;
      if (['failed', 'disconnected', 'closed'].includes(pc.connectionState)) {
        if (!connected.value) return;
        earcon('disconnect');
        cleanupRealtime();
        if (!reconnectTried) {
          reconnectTried = true;
          infoMsg.value = 'Bağlantı koptu, yeniden bağlanıyorum…';
          setTimeout(() => connectRealtime(), 1500);
        } else {
          fallbackToClassic('Ses bağlantısı tekrar koptu.');
        }
      }
    };

    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    const resp = await fetch(
      `https://api.openai.com/v1/realtime/calls?model=${encodeURIComponent(token.model)}`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token.value}`,
          'Content-Type': 'application/sdp',
        },
        body: offer.sdp,
      }
    );
    if (!resp.ok) throw new Error(`calls ${resp.status}`);
    const answerSdp = await resp.text();
    await pc.setRemoteDescription({ type: 'answer', sdp: answerSdp });
  } catch {
    fallbackToClassic('Gerçek zamanlı ses kurulamadı.');
  }
};

// BAS-KONUŞ: basılı tutarken dinler, bırakınca kaydı kapatıp cevabı ister.
// Çok kısa dokunuşta kayıt boş kalır ve sunucu "ses yok" hatası verir;
// o yüzden basılı tutma süresi ölçülür.
let pttBaslangic = 0;
const PTT_MIN_MS = 350;

const pttBasla = () => {
  if (konusmaModu.value !== 'bas_konus' || !connected.value) return;
  pttBaslangic = Date.now();
  pttBasili.value = true;
  status.value = 'listening';
  earcon('listen');
  if (navigator.vibrate) navigator.vibrate(30);
  dcSend({ type: 'input_audio_buffer.clear' });
  micAc(true);
};

const pttBitir = () => {
  if (konusmaModu.value !== 'bas_konus' || !pttBasili.value) return;
  pttBasili.value = false;
  micAc(false);
  if (Date.now() - pttBaslangic < PTT_MIN_MS) {
    dcSend({ type: 'input_audio_buffer.clear' });
    status.value = 'listening';
    infoMsg.value = 'Çok kısa bastınız — butonu konuşma boyunca basılı tutun.';
    return;
  }
  dcSend({ type: 'input_audio_buffer.commit' });
  dcSend({ type: 'response.create' });
  status.value = 'thinking';
  earcon('think');
};

// Mod değişince oturum yeniden kurulur: ses algılama ayarı sunucuda üretiliyor.
const moduDegistir = async yeni => {
  if (yeni === konusmaModu.value) return;
  konusmaModu.value = yeni;
  if (!connected.value) return;
  infoMsg.value = 'Konuşma modu değiştiriliyor…';
  manualClose = true;
  cleanupRealtime();
  manualClose = false;
  await connectRealtime();
};

const stopSpeakingRealtime = () => {
  dcSend({ type: 'response.cancel' });
  dcSend({ type: 'output_audio_buffer.clear' });
  status.value = 'listening';
};

const disconnectRealtime = () => {
  seviyeDonguDurdur();
  manualClose = true;
  earcon('disconnect');
  cleanupRealtime();
  status.value = 'idle';
  infoMsg.value = 'Oturum kapatıldı. Tekrar başlamak için butona dokunun.';
};

const sendTextRealtime = text => {
  question.value = text;
  dcSend({
    type: 'conversation.item.create',
    item: {
      type: 'message',
      role: 'user',
      content: [{ type: 'input_text', text }],
    },
  });
  dcSend({ type: 'response.create' });
};

// ---------------------------------------------------------------------------
// KLASİK MOD (yedek): tarayıcı STT → assistant → OpenAI TTS.
// DÜZELTME: cevap artık CÜMLE CÜMLE bölünüp sırayla okunur — eskiden tek
// çağrıda 1200 karakterde sessizce kesiliyordu (erişilebilirlik bug'ı).
// ---------------------------------------------------------------------------
let currentSource = null;
let speechSeq = 0;

const splitChunks = (text, max = 300) => {
  const sentences = text.match(/[^.!?…\n]+[.!?…]*\s*/g) || [text];
  const chunks = [];
  let cur = '';
  sentences.forEach(s => {
    if ((cur + s).length > max && cur) {
      chunks.push(cur);
      cur = s;
    } else {
      cur += s;
    }
  });
  if (cur.trim()) chunks.push(cur);
  return chunks.map(c => c.trim()).filter(Boolean);
};

const stopSpeakClassic = () => {
  speechSeq += 1; // sıradaki parçaları iptal et
  if (currentSource) {
    try {
      currentSource.onended = null;
      currentSource.stop();
    } catch {
      /* zaten durmuş */
    }
    currentSource = null;
  }
  if (status.value === 'speaking') status.value = 'idle';
};

const playChunk = (buf, seq) =>
  new Promise(resolve => {
    if (seq !== speechSeq || !audioCtx) {
      resolve();
      return;
    }
    audioCtx.decodeAudioData(buf).then(
      audioBuffer => {
        if (seq !== speechSeq) {
          resolve();
          return;
        }
        const source = audioCtx.createBufferSource();
        source.buffer = audioBuffer;
        const compressor = audioCtx.createDynamicsCompressor();
        const gain = audioCtx.createGain();
        gain.gain.value = 2.6;
        source.connect(compressor);
        compressor.connect(gain);
        gain.connect(audioCtx.destination);
        source.onended = () => {
          if (currentSource === source) currentSource = null;
          resolve();
        };
        currentSource = source;
        source.start(0);
      },
      () => resolve()
    );
  });

const speakClassic = async text => {
  if (!text) return;
  stopSpeakClassic();
  ensureAudio();
  if (!audioCtx) return;
  speechSeq += 1;
  const seq = speechSeq;
  status.value = 'speaking';
  const chunks = splitChunks(text);
  for (let i = 0; i < chunks.length; i += 1) {
    if (seq !== speechSeq) return;
    try {
      // eslint-disable-next-line no-await-in-loop
      const blob = await postMarketplaceBlob(
        accountId(),
        'meta_ads/report_speech',
        {
          text: chunks[i],
        }
      );
      // eslint-disable-next-line no-await-in-loop
      const buf = await blob.arrayBuffer();
      // eslint-disable-next-line no-await-in-loop
      await playChunk(buf, seq);
    } catch {
      /* parça okunamadı: sonrakine geç, metin ekranda */
    }
  }
  if (seq === speechSeq && status.value === 'speaking') status.value = 'idle';
};

const askClassic = async q => {
  const text = (q || '').trim();
  if (!text) return;
  question.value = text;
  answer.value = '';
  errorMsg.value = '';
  status.value = 'thinking';
  earcon('think');
  stopSpeakClassic();
  try {
    const data = await postMarketplaceJsonBody(
      accountId(),
      'meta_ads/assistant',
      {
        question: text,
        history: history.value.slice(-6),
      }
    );
    stopThinkTick();
    answer.value = data.answer || 'Cevap alınamadı.';
    history.value.push(
      { role: 'user', content: text },
      { role: 'assistant', content: answer.value }
    );
    if (history.value.length > 12) history.value = history.value.slice(-12);
    speakClassic(answer.value);
  } catch {
    stopThinkTick();
    status.value = 'idle';
    errorMsg.value = 'Cevap alınamadı, tekrar deneyin.';
    earcon('error');
    speakClassic('Üzgünüm, cevabı alamadım. Lütfen tekrar deneyin.');
  }
};

const SR =
  typeof window !== 'undefined' &&
  (window.SpeechRecognition || window.webkitSpeechRecognition);
let recognition = null;
let finalHandled = false;

const startListeningClassic = () => {
  ensureAudio();
  if (!SR) {
    errorMsg.value =
      'Bu tarayıcı sesli komutu desteklemiyor. Aşağıdan yazarak sorabilirsiniz.';
    return;
  }
  stopSpeakClassic();
  errorMsg.value = '';
  question.value = '';
  answer.value = '';
  finalHandled = false;
  recognition = new SR();
  recognition.lang = 'tr-TR';
  recognition.interimResults = true;
  recognition.maxAlternatives = 1;
  recognition.continuous = false;
  recognition.onresult = e => {
    const transcript = Array.from(e.results)
      .map(r => r[0].transcript)
      .join('')
      .trim();
    question.value = transcript;
    const last = e.results[e.results.length - 1];
    if (last.isFinal && !finalHandled && transcript) {
      finalHandled = true;
      try {
        recognition.stop();
      } catch {
        /* yok say */
      }
      askClassic(transcript);
    }
  };
  recognition.onerror = evt => {
    if (status.value === 'listening') status.value = 'idle';
    if (evt.error !== 'aborted' && evt.error !== 'no-speech') {
      errorMsg.value = 'Sesi anlayamadım, tekrar dener misiniz?';
      earcon('error');
    }
  };
  recognition.onend = () => {
    if (status.value === 'listening' && !finalHandled) status.value = 'idle';
  };
  try {
    recognition.start();
    status.value = 'listening';
    earcon('listen');
    if (navigator.vibrate) navigator.vibrate(40);
  } catch {
    status.value = 'idle';
  }
};

// ---------------------------------------------------------------------------
// ANA BUTON + ORTAK EYLEMLER
// ---------------------------------------------------------------------------
const onMainButton = () => {
  ensureAudio();
  if (mode.value === 'realtime') {
    if (!connected.value && status.value !== 'connecting') {
      connectRealtime();
    } else if (status.value === 'speaking') {
      stopSpeakingRealtime();
    } else if (konusmaModu.value !== 'bas_konus') {
      earcon('listen'); // bağlıyım ve dinliyorum işareti
    }
    return;
  }
  if (status.value === 'listening') {
    try {
      if (recognition) recognition.stop();
    } catch {
      /* yok say */
    }
    status.value = 'idle';
  } else if (status.value === 'speaking') {
    stopSpeakClassic();
  } else if (status.value === 'idle' || status.value === 'error') {
    startListeningClassic();
  }
};

const submitText = () => {
  ensureAudio();
  const t = textInput.value.trim();
  if (!t) return;
  textInput.value = '';
  if (mode.value === 'realtime' && connected.value) sendTextRealtime(t);
  else askClassic(t);
};

const askExample = ex => {
  ensureAudio();
  if (mode.value === 'realtime' && connected.value) sendTextRealtime(ex);
  else askClassic(ex);
};

const replayClassic = () => {
  ensureAudio();
  if (status.value === 'speaking') stopSpeakClassic();
  else if (answer.value) speakClassic(answer.value);
};

const retryRealtime = () => {
  mode.value = 'realtime';
  reconnectTried = false;
  connectRealtime();
};

// Halkanın canlı büyümesi: dinlerken mikrofon gücü, konuşurken asistanın sesi.
const halkaOlcek = computed(() => {
  const seviye =
    status.value === 'speaking' ? sesSeviye.value : micSeviye.value;
  return 1 + Math.min(0.18, seviye * 0.25);
});

const halkaRengi = computed(() => {
  if (status.value === 'listening') return 'from-n-teal-9 to-n-teal-11';
  if (status.value === 'thinking' || status.value === 'connecting')
    return 'from-n-amber-9 to-n-amber-11';
  if (status.value === 'speaking') return 'from-n-brand to-n-violet-11';
  if (status.value === 'error') return 'from-n-ruby-9 to-n-ruby-11';
  return 'from-n-slate-8 to-n-slate-10';
});

const durumRozeti = computed(() => {
  if (!connected.value && mode.value === 'realtime') return 'Bağlı değil';
  if (status.value === 'listening')
    return konusmaModu.value === 'bas_konus' && !pttBasili.value
      ? 'Hazır — basılı tut ve konuş'
      : 'Dinliyorum';
  if (status.value === 'thinking') return 'Düşünüyorum';
  if (status.value === 'speaking') return 'Konuşuyorum';
  if (status.value === 'connecting') return 'Bağlanıyorum';
  if (status.value === 'error') return 'Sorun var';
  return 'Hazır';
});

const buttonLabel = computed(() => {
  if (mode.value === 'realtime') {
    if (status.value === 'connecting') return 'Bağlanıyorum…';
    if (!connected.value) return 'Konuşmayı Başlat';
    if (status.value === 'speaking')
      return 'Konuşuyorum… (susturmak için dokun)';
    if (status.value === 'thinking') return 'Düşünüyorum…';
    return 'Dinliyorum… konuşun';
  }
  if (status.value === 'listening') return 'Dinliyorum… (durdurmak için bas)';
  if (status.value === 'thinking') return 'Düşünüyorum…';
  if (status.value === 'speaking') return 'Konuşuyorum… (durdurmak için bas)';
  return 'Sormak için bas';
});

const displayedAnswer = computed(() =>
  mode.value === 'realtime' && liveAnswer.value
    ? liveAnswer.value
    : answer.value
);

// Boşluk tuşu = bas-konuş (klavye kullananlar ve erişilebilirlik için).
const tusBasildi = e => {
  if (e.code !== 'Space' || e.repeat) return;
  if (konusmaModu.value !== 'bas_konus' || !connected.value) return;
  const hedef = e.target;
  if (hedef && ['INPUT', 'TEXTAREA'].includes(hedef.tagName)) return;
  e.preventDefault();
  pttBasla();
};

const tusBirakildi = e => {
  if (e.code !== 'Space') return;
  if (konusmaModu.value !== 'bas_konus') return;
  e.preventDefault();
  pttBitir();
};

onMounted(() => {
  window.addEventListener('keydown', tusBasildi);
  window.addEventListener('keyup', tusBirakildi);
});

onBeforeUnmount(() => {
  window.removeEventListener('keydown', tusBasildi);
  window.removeEventListener('keyup', tusBirakildi);
  seviyeDonguDurdur();
  manualClose = true;
  cleanupRealtime();
  stopSpeakClassic();
  stopThinkTick();
  if (recognition) {
    try {
      recognition.abort();
    } catch {
      /* yok say */
    }
  }
});
</script>

<template>
  <div
    class="flex flex-col items-center w-full h-full overflow-hidden bg-n-background"
  >
    <header
      class="flex-shrink-0 w-full px-5 py-4 text-center border-b sm:px-7 border-n-weak"
    >
      <div class="flex items-center justify-center gap-3">
        <h1 class="text-2xl font-bold text-n-slate-12">Sesli Asistan</h1>
        <span
          class="px-3 py-1 text-sm font-semibold rounded-full"
          :class="
            connected
              ? 'bg-n-teal-3 text-n-teal-11'
              : 'bg-n-slate-3 text-n-slate-11'
          "
        >
          {{ durumRozeti }}
        </span>
      </div>

      <!-- KONUŞMA MODU: gürültülü ortamda sözünün kesilmesini bitiren ayar -->
      <div
        v-if="mode === 'realtime'"
        class="flex flex-wrap items-center justify-center gap-2 mt-3"
      >
        <button
          v-for="m in KONUSMA_MODLARI"
          :key="m.key"
          type="button"
          :title="m.ipucu"
          class="px-4 py-2 text-base font-semibold rounded-full border transition-colors"
          :class="
            konusmaModu === m.key
              ? 'bg-n-brand text-white border-transparent'
              : 'bg-n-solid-2 text-n-slate-11 border-n-weak hover:bg-n-alpha-1'
          "
          @click="moduDegistir(m.key)"
        >
          {{ m.label }}
        </button>
      </div>
      <p class="mt-2 text-base text-n-slate-11">
        {{
          KONUSMA_MODLARI.find(m => m.key === konusmaModu)?.ipucu ||
          'Klasik mod (yedek): butona bas, sorunu sor'
        }}
      </p>
    </header>

    <div
      ref="akisKutusu"
      class="flex flex-col items-center flex-1 w-full max-w-2xl px-5 py-6 overflow-y-auto"
    >
      <!-- Ekran okuyucu + görme destekli CANLI DURUM (her değişiklik okunur) -->
      <p
        aria-live="assertive"
        role="status"
        class="mb-4 text-xl font-semibold text-center text-n-slate-11"
      >
        {{ statusText }}
      </p>

      <!-- CANLI SES HALKASI: dinlerken mikrofon gücüyle, konuşurken asistanın
           sesiyle büyür. "Beni duyuyor mu" sorusunun görsel cevabı. -->
      <div class="relative flex items-center justify-center my-2">
        <span
          class="absolute rounded-full size-72 sm:size-80 opacity-20 blur-2xl bg-gradient-to-br transition-transform duration-150"
          :class="halkaRengi"
          :style="{ transform: `scale(${halkaOlcek})` }"
        />
        <span
          v-if="status === 'listening' || status === 'speaking'"
          class="absolute rounded-full size-56 sm:size-64 border-4 opacity-40 transition-transform duration-150"
          :class="status === 'speaking' ? 'border-n-brand' : 'border-n-teal-9'"
          :style="{ transform: `scale(${halkaOlcek + 0.06})` }"
        />
        <button
          type="button"
          :aria-label="buttonLabel"
          class="relative flex flex-col items-center justify-center rounded-full size-52 sm:size-60 flex-shrink-0 outline-none focus:ring-4 focus:ring-n-brand/40 text-white bg-gradient-to-br shadow-xl transition-transform duration-150 select-none"
          :class="[
            halkaRengi,
            pttBasili ? 'scale-95 ring-4 ring-white/30' : 'hover:opacity-95',
          ]"
          :style="{ transform: `scale(${pttBasili ? 0.96 : halkaOlcek})` }"
          @click="onMainButton"
          @pointerdown="pttBasla"
          @pointerup="pttBitir"
          @pointerleave="pttBitir"
          @pointercancel="pttBitir"
        >
          <Spinner
            v-if="status === 'thinking' || status === 'connecting'"
            class="size-16"
          />
          <span
            v-else
            class="size-20"
            :class="
              status === 'speaking' ? 'i-lucide-volume-2' : 'i-lucide-mic'
            "
          />
          <span class="relative px-4 mt-2 text-lg font-bold text-center">
            {{ buttonLabel }}
          </span>
        </button>
      </div>

      <!-- Mikrofon seviye çubuğu: sesin gerçekten gittiğini gösterir -->
      <div
        v-if="connected && mode === 'realtime'"
        class="w-full max-w-xs h-2 mt-1 overflow-hidden rounded-full bg-n-solid-2"
        :aria-label="`Mikrofon seviyesi yüzde ${Math.round(micSeviye * 100)}`"
      >
        <div
          class="h-full rounded-full bg-n-teal-9 transition-[width] duration-100"
          :style="{ width: `${Math.min(100, micSeviye * 140)}%` }"
        />
      </div>

      <!-- Hangi veriye bakıldığı: sessiz bekleme hissini bitirir -->
      <p
        v-if="aracEtiketi"
        aria-live="polite"
        class="mt-3 text-lg font-semibold text-center text-n-amber-11"
      >
        📊 {{ aracEtiketi }} verilerine bakıyorum…
      </p>

      <!-- Oturum kapat (realtime bağlıyken) -->
      <button
        v-if="mode === 'realtime' && connected"
        type="button"
        class="px-6 py-3 mt-4 text-lg font-semibold text-white rounded-xl bg-n-ruby-9"
        @click="disconnectRealtime"
      >
        Oturumu Kapat
      </button>

      <!-- Klasik moddaysa gerçek zamanlıya dönüş -->
      <button
        v-if="mode === 'classic'"
        type="button"
        class="px-6 py-3 mt-4 text-lg font-semibold text-white rounded-xl bg-n-brand"
        @click="retryRealtime"
      >
        Gerçek Zamanlı Moda Geç
      </button>

      <!-- Bilgi / hata (yüksek kontrast, büyük) -->
      <p
        v-if="infoMsg"
        aria-live="polite"
        class="mt-5 text-lg text-center text-n-slate-11"
      >
        {{ infoMsg }}
      </p>
      <p
        v-if="errorMsg"
        aria-live="assertive"
        class="mt-5 text-xl text-center text-n-ruby-11"
      >
        {{ errorMsg }}
      </p>

      <!-- KONUŞMA DÖKÜMÜ: sadece son cevap değil, tüm konuşma akışı.
           Sesli dinlerken kaçırılan yer geri okunabilsin diye. -->
      <div v-if="dokum.length" class="w-full mt-6 space-y-3">
        <div
          v-for="satir in dokum"
          :key="satir.id"
          class="flex"
          :class="satir.rol === 'soru' ? 'justify-end' : 'justify-start'"
        >
          <div
            class="max-w-[92%] rounded-3xl px-5 py-4"
            :class="
              satir.rol === 'soru'
                ? 'bg-n-brand/10 outline outline-1 outline-n-brand/30'
                : 'bg-n-solid-2 outline outline-1 outline-n-weak'
            "
          >
            <p
              class="mb-1 text-sm font-semibold"
              :class="satir.rol === 'soru' ? 'text-n-brand' : 'text-n-slate-10'"
            >
              {{ satir.rol === 'soru' ? 'Siz' : 'Asistan' }}
            </p>
            <p
              class="leading-relaxed text-n-slate-12"
              :class="satir.rol === 'soru' ? 'text-xl' : 'text-2xl'"
            >
              {{ satir.metin }}
            </p>
          </div>
        </div>
      </div>

      <!-- Akmakta olan cevap (henüz bitmedi) -->
      <div
        v-if="status === 'speaking' && liveAnswer"
        class="w-full mt-3 px-5 py-4 rounded-3xl bg-n-solid-2 outline outline-1 outline-n-weak"
      >
        <p class="mb-1 text-sm font-semibold text-n-slate-10">Asistan…</p>
        <p class="text-2xl leading-relaxed text-n-slate-12">{{ liveAnswer }}</p>
      </div>

      <!-- Klasik modda tekrar dinletme -->
      <div
        v-if="mode === 'classic' && displayedAnswer"
        class="flex justify-center mt-4"
      >
        <button
          type="button"
          class="flex items-center gap-2 px-6 py-3 text-lg font-semibold text-white rounded-xl"
          :class="status === 'speaking' ? 'bg-n-ruby-9' : 'bg-n-teal-9'"
          @click="replayClassic"
        >
          <span
            :class="
              status === 'speaking' ? 'i-lucide-square' : 'i-lucide-volume-2'
            "
            class="size-6"
          />
          {{ status === 'speaking' ? 'Durdur' : 'Tekrar Dinle' }}
        </button>
      </div>

      <!-- Yazarak sorma (yedek giriş yolu) -->
      <form class="flex w-full gap-2 mt-8" @submit.prevent="submitText">
        <input
          v-model="textInput"
          type="text"
          aria-label="Yazarak soru sor"
          placeholder="Ya da buraya yazarak sor…"
          class="flex-1 px-4 py-3 text-lg rounded-xl bg-n-solid-2 text-n-slate-12 outline outline-1 outline-n-weak"
        />
        <button
          type="submit"
          class="px-5 py-3 text-lg font-semibold text-white rounded-xl bg-n-brand disabled:opacity-50"
          :disabled="!textInput.trim()"
        >
          Sor
        </button>
      </form>

      <!-- Örnek sorular -->
      <div class="w-full mt-6">
        <p class="mb-2 text-base text-n-slate-11">Örnek sorular:</p>
        <div class="flex flex-wrap gap-2">
          <button
            v-for="ex in [
              'Günaydın, bugünkü brifingi ver',
              'Bugünü değerlendir, dünle kıyasla',
              'Şirket şu an kârda mı zararda mı?',
              'Satış botu dün nasıl çalıştı, cevapsız müşteri var mı?',
              'Kargolar ne durumda, teslim edilemeyen var mı?',
              'Hangi kanal daha iyi gidiyor?',
              'İşimin röntgenini çek, nereye odaklanmalıyım?',
            ]"
            :key="ex"
            type="button"
            class="px-4 py-2 text-base rounded-full bg-n-solid-2 text-n-slate-12 hover:bg-n-alpha-1"
            @click="askExample(ex)"
          >
            {{ ex }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
